;(() => {
  const VISITOR_HEADER = 'X-Evidence-Visitor-Id';
  const COOKIE_KIND = 'cookie_decision';
  const POLICY_KIND = 'policy_acknowledgement';
  const PROCESSING_KIND = 'personal_data_processing_consent';
  const REQUIRED_COOKIE_DECISION = 'rejected_all';
  const ANALYTICS_COOKIE_CATEGORY = 'analytics';
  const MARKETING_COOKIE_CATEGORY = 'marketing';
  const state = {
    config: null,
    root: null,
    consent: null,
    cookieMetadata: null,
    currentReceipts: [],
    currentEvidenceAvailable: false,
    visitorToken: '',
    visitorTokenExpiresAt: 0,
    pendingIdempotencyKeys: new Map(),
    pendingIdempotencyLoaded: false,
    initialized: false,
    lastSettingsTrigger: null,
    pendingSettingsOptions: null,
    metadataPromises: new Map()
  };

  const clone = (value) => (
    value === null || value === undefined
      ? value
      : JSON.parse(JSON.stringify(value))
  );

  const getConsent = () => clone(state.consent);
  const hasConsent = (category) => (
    category === 'externalServices'
    && state.consent?.externalServices === true
    && state.consent?.evidenceStatus === 'recorded'
  );

  const getLegalConsentState = () => {
    const policy = findCurrentReceipt(POLICY_KIND);
    const processing = findCurrentReceipt(
      PROCESSING_KIND,
      [state.config?.personalDataPurposeId].filter(Boolean)
    );

    return {
      policyAcknowledged: policy?.decision === 'acknowledged',
      processingConsentGranted: processing?.decision === 'granted'
    };
  };

  const publicApi = {
    getConsent,
    hasConsent,
    getLegalConsentState,
    setConsent: (nextConsent) => saveCookieChoice(
      Boolean(nextConsent?.externalServices),
      { source: 'api' }
    ),
    openConsentSettings: (options = {}) => openSettings(options),
    ensureLegalEvidence: (options) => ensureLegalEvidence(options),
    resolveApiUrl: (endpoint) => resolveApiUrl(endpoint)
  };

  window.KF21ConsentManager = Object.freeze(publicApi);

  const createUserError = (message, code = 'CONSENT_ERROR', cause = null) => {
    const error = new Error(message, cause ? { cause } : undefined);
    error.code = code;
    error.userMessage = message;
    return error;
  };

  const safeStorageGet = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const safeStorageSet = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  };

  const safeStorageRemove = (key) => {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      // Storage may be unavailable in privacy modes. Fail closed.
    }
  };

  const createIdempotencyKey = (prefix) => {
    if (typeof window.crypto?.randomUUID === 'function') {
      return `${prefix}-${window.crypto.randomUUID()}`;
    }

    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    const token = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
    return `${prefix}-${token}`;
  };

  const loadPendingIdempotencyKeys = () => {
    if (state.pendingIdempotencyLoaded || !state.config) {
      return;
    }

    state.pendingIdempotencyLoaded = true;
    const raw = safeStorageGet(state.config.idempotencyStorageKey);
    if (!raw) {
      return;
    }

    try {
      const stored = JSON.parse(raw);
      Object.entries(stored).forEach(([operationId, idempotencyKey]) => {
        if (
          typeof operationId === 'string'
          && typeof idempotencyKey === 'string'
          && operationId
          && idempotencyKey
        ) {
          state.pendingIdempotencyKeys.set(operationId, idempotencyKey);
        }
      });
    } catch (error) {
      safeStorageRemove(state.config.idempotencyStorageKey);
    }
  };

  const persistPendingIdempotencyKeys = () => {
    if (!state.config) {
      return;
    }

    if (state.pendingIdempotencyKeys.size === 0) {
      safeStorageRemove(state.config.idempotencyStorageKey);
      return;
    }

    safeStorageSet(
      state.config.idempotencyStorageKey,
      JSON.stringify(Object.fromEntries(state.pendingIdempotencyKeys))
    );
  };

  const getPendingIdempotencyKey = (operationId, prefix) => {
    loadPendingIdempotencyKeys();
    const existing = state.pendingIdempotencyKeys.get(operationId);
    if (existing) {
      return existing;
    }

    const idempotencyKey = createIdempotencyKey(prefix);
    state.pendingIdempotencyKeys.set(operationId, idempotencyKey);
    persistPendingIdempotencyKeys();
    return idempotencyKey;
  };

  const clearPendingIdempotencyKey = (operationId, idempotencyKey = '') => {
    loadPendingIdempotencyKeys();
    const current = state.pendingIdempotencyKeys.get(operationId);
    if (!current || (idempotencyKey && current !== idempotencyKey)) {
      return;
    }

    state.pendingIdempotencyKeys.delete(operationId);
    persistPendingIdempotencyKeys();
  };

  const clearPendingIdempotencyPrefix = (prefix) => {
    loadPendingIdempotencyKeys();
    let changed = false;
    state.pendingIdempotencyKeys.forEach((value, operationId) => {
      if (operationId.startsWith(prefix)) {
        state.pendingIdempotencyKeys.delete(operationId);
        changed = true;
      }
    });
    if (changed) {
      persistPendingIdempotencyKeys();
    }
  };

  const resolveApiUrl = (endpoint, apiBaseUrl = state.config?.apiBaseUrl || '') => {
    const value = String(endpoint || '').trim();
    const base = String(apiBaseUrl || '').trim().replace(/\/+$/, '');
    if (!value || !base || /^(?:[a-z]+:)?\/\//i.test(value)) {
      return value;
    }

    try {
      const normalizedBase = new URL(`${base}/`, document.baseURI);
      return new URL(value.replace(/^\/+/, ''), normalizedBase).toString();
    } catch (error) {
      return value;
    }
  };

  const readVisitorToken = () => {
    if (
      state.visitorToken
      && state.visitorTokenExpiresAt > Date.now()
    ) {
      return state.visitorToken;
    }
    if (!state.config) {
      return '';
    }

    state.visitorToken = '';
    state.visitorTokenExpiresAt = 0;
    const raw = safeStorageGet(state.config.visitorStorageKey)?.trim() || '';
    if (!raw) {
      return '';
    }

    try {
      const stored = JSON.parse(raw);
      const expiresAt = Date.parse(stored?.expiresAt);
      if (
        typeof stored?.token !== 'string'
        || !stored.token.trim()
        || !Number.isFinite(expiresAt)
        || expiresAt <= Date.now()
      ) {
        safeStorageRemove(state.config.visitorStorageKey);
        return '';
      }

      state.visitorToken = stored.token.trim();
      state.visitorTokenExpiresAt = expiresAt;
    } catch (error) {
      const expiresAt = Date.now()
        + (state.config.visitorExpiresInDays * 24 * 60 * 60 * 1000);
      state.visitorToken = raw;
      state.visitorTokenExpiresAt = expiresAt;
      safeStorageSet(state.config.visitorStorageKey, JSON.stringify({
        token: raw,
        expiresAt: new Date(expiresAt).toISOString()
      }));
    }
    return state.visitorToken;
  };

  const storeVisitorToken = (response) => {
    const token = response.headers.get(VISITOR_HEADER)?.trim();
    if (token && state.config) {
      const expiresAt = Date.now()
        + (state.config.visitorExpiresInDays * 24 * 60 * 60 * 1000);
      state.visitorToken = token;
      state.visitorTokenExpiresAt = expiresAt;
      safeStorageSet(state.config.visitorStorageKey, JSON.stringify({
        token,
        expiresAt: new Date(expiresAt).toISOString()
      }));
    }
  };

  const evidenceHeaders = (idempotencyKey = '') => {
    const headers = new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json'
    });
    const visitorToken = readVisitorToken();

    if (idempotencyKey) {
      headers.set('Idempotency-Key', idempotencyKey);
    }
    if (visitorToken) {
      headers.set(VISITOR_HEADER, visitorToken);
    }

    return headers;
  };

  const readErrorDetail = async (response) => {
    try {
      const body = await response.clone().json();
      if (typeof body?.detail === 'string') {
        return body.detail;
      }

      const values = Object.values(body || {}).flat().filter((value) => (
        typeof value === 'string'
      ));
      return values.join(' ');
    } catch (error) {
      return '';
    }
  };

  const requestJson = async (url, requestInit = {}) => {
    let response;

    try {
      response = await fetch(url, {
        cache: 'no-store',
        ...requestInit
      });
    } catch (error) {
      throw createUserError(
        'Не удалось связаться с сервисом Evidence. Внешние сервисы остаются выключенными.',
        'EVIDENCE_NETWORK',
        error
      );
    }

    storeVisitorToken(response);

    if (!response.ok) {
      const detail = await readErrorDetail(response);
      const error = createUserError(
        response.status === 404
          ? 'Актуальная версия юридического материала пока не опубликована.'
          : response.status === 409
            ? 'Версия юридических материалов изменилась. Обновляем состояние — повторите действие.'
            : response.status === 429
              ? 'Слишком много запросов. Подождите немного и повторите действие.'
              : 'Сервис Evidence временно не принял запрос. Внешние сервисы остаются выключенными.',
        'EVIDENCE_HTTP'
      );
      error.status = response.status;
      error.detail = detail;
      throw error;
    }

    try {
      return await response.json();
    } catch (error) {
      throw createUserError(
        'Сервис Evidence вернул некорректный ответ. Внешние сервисы остаются выключенными.',
        'EVIDENCE_RESPONSE',
        error
      );
    }
  };

  const normalizeReceipt = (receipt) => (
    receipt
    && typeof receipt.evidenceId === 'string'
    && typeof receipt.kind === 'string'
    && typeof receipt.decision === 'string'
      ? receipt
      : null
  );

  const updateCurrentReceipt = (receipt) => {
    const normalized = normalizeReceipt(receipt);
    if (!normalized) {
      return;
    }

    state.currentReceipts = state.currentReceipts.filter((item) => (
      item.evidenceId !== normalized.evidenceId
      && item.evidenceId !== normalized.supersedesEvidenceId
    ));
    state.currentReceipts.push(normalized);
    state.currentEvidenceAvailable = true;
  };

  const refreshCurrentEvidence = async () => {
    const visitorToken = readVisitorToken();
    if (!visitorToken) {
      state.currentReceipts = [];
      state.currentEvidenceAvailable = true;
      return [];
    }

    try {
      const receipts = await requestJson(state.config.currentConsentsUrl, {
        method: 'GET',
        headers: evidenceHeaders()
      });
      state.currentReceipts = Array.isArray(receipts)
        ? receipts.map(normalizeReceipt).filter(Boolean)
        : [];
      state.currentEvidenceAvailable = true;
      return state.currentReceipts;
    } catch (error) {
      if (error.status === 401) {
        safeStorageRemove(state.config.visitorStorageKey);
        state.visitorToken = '';
        state.currentReceipts = [];
      }
      state.currentEvidenceAvailable = false;
      throw error;
    }
  };

  const sameStringSet = (first = [], second = []) => (
    first.length === second.length
    && [...first].sort().every((value, index) => value === [...second].sort()[index])
  );

  function findCurrentReceipt(kind, purposeIds = null) {
    return state.currentReceipts.find((receipt) => {
      if (receipt.kind !== kind) {
        return false;
      }

      if (!purposeIds) {
        return true;
      }

      return sameStringSet(receipt.purposeIds || [], purposeIds);
    }) || null;
  }

  const metadataDefinition = (name) => state.config.materials[name];

  const validateMetadata = (metadata, definition) => {
    if (
      !metadata
      || metadata.materialType !== definition.materialType
      || typeof metadata.materialId !== 'string'
      || typeof metadata.versionId !== 'string'
      || typeof metadata.contentUrl !== 'string'
      || !metadata.versionId
    ) {
      throw createUserError(
        'Сервис Evidence вернул неполные сведения о юридическом материале.',
        'LEGAL_METADATA_INVALID'
      );
    }

    return {
      ...metadata,
      contentUrl: resolveApiUrl(metadata.contentUrl)
    };
  };

  const getMetadata = (name, force = false) => {
    if (force) {
      state.metadataPromises.delete(name);
    }
    if (state.metadataPromises.has(name)) {
      return state.metadataPromises.get(name);
    }

    const definition = metadataDefinition(name);
    const promise = requestJson(definition.metadataUrl, {
      method: 'GET',
      headers: new Headers({ Accept: 'application/json' })
    })
      .then((metadata) => validateMetadata(metadata, definition))
      .catch((error) => {
        state.metadataPromises.delete(name);
        throw error;
      });

    state.metadataPromises.set(name, promise);
    return promise;
  };

  const getCookieMetadata = async (force = false) => {
    const [policy, registry] = await Promise.all([
      getMetadata('cookiePolicy', force),
      getMetadata('cookieRegistry', force)
    ]);

    state.cookieMetadata = { policy, registry };
    return state.cookieMetadata;
  };

  const receiptHasVersion = (receipt, metadata) => (
    Array.isArray(receipt?.legalMaterials)
    && receipt.legalMaterials.some((material) => (
      material.materialId === metadata.materialId
      && material.versionId === metadata.versionId
    ))
  );

  const readStoredConsent = () => {
    const raw = safeStorageGet(state.config.storageKey);
    if (!raw) {
      return null;
    }

    try {
      const value = JSON.parse(raw);
      const expiresAt = Date.parse(value.expiresAt);
      if (
        value.schemaVersion !== state.config.schemaVersion
        || value.necessary !== true
        || typeof value.externalServices !== 'boolean'
        || !Number.isFinite(expiresAt)
        || expiresAt <= Date.now()
      ) {
        safeStorageRemove(state.config.storageKey);
        return null;
      }

      return value;
    } catch (error) {
      safeStorageRemove(state.config.storageKey);
      return null;
    }
  };

  const createStoredConsent = ({
    externalServices,
    evidenceStatus,
    evidenceId = null,
    metadata = state.cookieMetadata,
    pendingEvidence = null
  }) => {
    const decidedAt = new Date();
    const expiresAt = new Date(
      decidedAt.getTime() + (state.config.expiresInDays * 24 * 60 * 60 * 1000)
    );

    return {
      schemaVersion: state.config.schemaVersion,
      policyVersion: metadata?.policy?.versionId || null,
      categoryRegistryVersion: metadata?.registry?.versionId || null,
      necessary: true,
      externalServices,
      decidedAt: decidedAt.toISOString(),
      expiresAt: expiresAt.toISOString(),
      evidenceStatus,
      evidenceId,
      pendingEvidence
    };
  };

  const writeStoredConsent = (consent) => {
    safeStorageSet(state.config.storageKey, JSON.stringify(consent));
  };

  const dispatchConsentEvent = (eventName) => {
    window.dispatchEvent(new CustomEvent(eventName, {
      detail: getConsent()
    }));
  };

  const applyConsent = (consent, eventName = 'kf21:consent-changed') => {
    state.consent = consent;
    document.documentElement.dataset.cookieEvidenceStatus = (
      consent?.evidenceStatus || 'missing'
    );
    dispatchConsentEvent(eventName);
  };

  const storedVersionsMatch = (consent, metadata) => (
    consent?.policyVersion === metadata?.policy?.versionId
    && consent?.categoryRegistryVersion === metadata?.registry?.versionId
  );

  const isPositiveCookieReceipt = (receipt) => {
    if (!receipt || receipt.kind !== COOKIE_KIND || receipt.decision === 'withdrawn') {
      return false;
    }

    return Array.isArray(receipt.cookieDecisions)
      && receipt.cookieDecisions.some((decision) => (
        decision.category === state.config.externalServiceCategory
        && decision.decision === 'granted'
        && decision.legalBasis === 'consent'
      ));
  };

  const hasCookieDecision = (receipt, category, decision) => (
    Array.isArray(receipt?.cookieDecisions)
    && receipt.cookieDecisions.some((item) => (
      item.category === category
      && item.decision === decision
    ))
  );

  const matchesGrantedExternalServicesSnapshot = (receipt) => (
    isPositiveCookieReceipt(receipt)
    && hasCookieDecision(receipt, ANALYTICS_COOKIE_CATEGORY, 'denied')
    && hasCookieDecision(receipt, MARKETING_COOKIE_CATEGORY, 'denied')
  );

  const createGrantedExternalServicesSnapshot = () => ([
    {
      category: ANALYTICS_COOKIE_CATEGORY,
      decision: 'denied'
    },
    {
      category: MARKETING_COOKIE_CATEGORY,
      decision: 'denied'
    },
    {
      category: state.config.externalServiceCategory,
      decision: 'granted'
    }
  ]);

  const canRestorePositiveConsent = (stored, metadata) => {
    const current = findCurrentReceipt(COOKIE_KIND);

    return (
      state.config.externalServiceEvidenceSupported
      && stored?.externalServices === true
      && stored.evidenceStatus === 'recorded'
      && storedVersionsMatch(stored, metadata)
      && state.currentEvidenceAvailable
      && current?.evidenceId === stored.evidenceId
      && receiptHasVersion(current, metadata.policy)
      && receiptHasVersion(current, metadata.registry)
      && isPositiveCookieReceipt(current)
    );
  };

  const setStatus = (element, message = '', stateName = 'info') => {
    if (!element) {
      return;
    }

    element.textContent = message;
    element.hidden = !message;
    element.dataset.state = stateName;
    element.setAttribute('role', stateName === 'error' ? 'alert' : 'status');
  };

  const getElements = () => ({
    banner: state.root.querySelector('[data-cookie-consent-banner]'),
    bannerStatus: state.root.querySelector('[data-cookie-banner-status]'),
    dialog: state.root.querySelector('[data-cookie-settings-dialog]'),
    dialogPanel: state.root.querySelector('[data-cookie-settings-panel]'),
    dialogStatus: state.root.querySelector('[data-cookie-dialog-status]'),
    externalToggle: state.root.querySelector('[data-cookie-external-toggle]'),
    externalCategory: state.root.querySelector('[data-cookie-external-category]'),
    busyButtons: state.root.querySelectorAll(
      '[data-cookie-accept-all], [data-cookie-necessary-only], '
      + '[data-cookie-save-settings], [data-cookie-dialog-accept-all], '
      + '[data-cookie-dialog-necessary-only]'
    )
  });

  const showBanner = (message = '', stateName = 'info') => {
    const { banner, bannerStatus } = getElements();
    banner.hidden = false;
    setStatus(bannerStatus, message, stateName);
  };

  const hideBanner = () => {
    getElements().banner.hidden = true;
  };

  const setCookieBusy = (isBusy) => {
    const { dialog, busyButtons } = getElements();
    busyButtons.forEach((button) => {
      button.disabled = isBusy || button.dataset.cookieUnavailable === 'true';
    });
    dialog.setAttribute('aria-busy', String(isBusy));
  };

  const captureEvidence = async (payload, idempotencyKey) => {
    const receipt = await requestJson(state.config.consentsUrl, {
      method: 'POST',
      headers: evidenceHeaders(idempotencyKey),
      body: JSON.stringify(payload)
    });
    const normalized = normalizeReceipt(receipt);

    if (!normalized) {
      throw createUserError(
        'Сервис Evidence не вернул подтверждение записи.',
        'EVIDENCE_RECEIPT_INVALID'
      );
    }

    updateCurrentReceipt(normalized);
    window.dispatchEvent(new CustomEvent('kf21:evidence-changed', {
      detail: clone(normalized)
    }));
    return normalized;
  };

  const withdrawEvidence = async (evidenceId, idempotencyKey) => {
    try {
      const receipt = await requestJson(state.config.withdrawalsUrl, {
        method: 'POST',
        headers: evidenceHeaders(idempotencyKey),
        body: JSON.stringify({ evidenceId })
      });
      const normalized = normalizeReceipt(receipt);

      if (
        !normalized
        || normalized.decision !== 'withdrawn'
        || normalized.supersedesEvidenceId !== evidenceId
      ) {
        throw createUserError(
          'Сервис Evidence не вернул подтверждение отзыва.',
          'EVIDENCE_WITHDRAWAL_INVALID'
        );
      }

      updateCurrentReceipt(normalized);
      window.dispatchEvent(new CustomEvent('kf21:evidence-changed', {
        detail: clone(normalized)
      }));
      return normalized;
    } catch (error) {
      if (error.status !== 409 || !readVisitorToken()) {
        throw error;
      }

      await refreshCurrentEvidence();
      const current = findCurrentReceipt(COOKIE_KIND);
      if (
        current?.decision === 'withdrawn'
        && current.supersedesEvidenceId === evidenceId
      ) {
        return current;
      }

      throw error;
    }
  };

  const captureWithConflictRecovery = async (
    payload,
    idempotencyKey,
    matchesCurrent
  ) => {
    try {
      const receipt = await captureEvidence(payload, idempotencyKey);
      if (!matchesCurrent(receipt)) {
        throw createUserError(
          'Сервис Evidence вернул подтверждение, не соответствующее отправленному выбору.',
          'EVIDENCE_RECEIPT_MISMATCH'
        );
      }
      return receipt;
    } catch (error) {
      if (error.status !== 409 || !readVisitorToken()) {
        throw error;
      }

      await refreshCurrentEvidence();
      const current = findCurrentReceipt(
        payload.kind,
        payload.kind === PROCESSING_KIND ? payload.purposeIds : null
      );
      if (current && matchesCurrent(current)) {
        return current;
      }

      throw error;
    }
  };

  const cookieLegalVersions = (metadata) => ([
    {
      materialType: state.config.materials.cookiePolicy.materialType,
      versionId: metadata.policy.versionId
    },
    {
      materialType: state.config.materials.cookieRegistry.materialType,
      versionId: metadata.registry.versionId
    }
  ]);

  const createCookieOperationId = (externalServices, metadata, current) => {
    const action = externalServices
      ? 'grant'
      : isPositiveCookieReceipt(current)
        ? 'withdraw'
        : 'reject';

    return [
      'cookie',
      action,
      metadata.policy.versionId,
      metadata.registry.versionId,
      current?.evidenceId || 'root'
    ].join('|');
  };

  const recordCookieEvidence = async (
    externalServices,
    metadata,
    idempotencyKey
  ) => {
    const current = findCurrentReceipt(COOKIE_KIND);

    if (externalServices && !state.config.externalServiceEvidenceSupported) {
      throw createUserError(
        'Интерактивную карту пока нельзя включить: Evidence ещё не поддерживает категорию «Внешние сервисы».',
        'COOKIE_CATEGORY_CONTRACT_BLOCKED'
      );
    }

    if (!externalServices && isPositiveCookieReceipt(current)) {
      return withdrawEvidence(current.evidenceId, idempotencyKey);
    }

    const payload = {
      kind: COOKIE_KIND,
      context: state.config.context,
      decision: externalServices
        ? (current ? 'changed' : 'saved_granular')
        : REQUIRED_COOKIE_DECISION,
      locale: state.config.locale,
      purposeIds: null,
      cookieDecisions: externalServices
        ? createGrantedExternalServicesSnapshot()
        : null,
      legalVersions: cookieLegalVersions(metadata),
      supersedesEvidenceId: current?.evidenceId || null
    };

    return captureWithConflictRecovery(
      payload,
      idempotencyKey,
      (receipt) => (
        receiptHasVersion(receipt, metadata.policy)
        && receiptHasVersion(receipt, metadata.registry)
        && (
          externalServices
            ? matchesGrantedExternalServicesSnapshot(receipt)
            : receipt.decision === REQUIRED_COOKIE_DECISION
        )
      )
    );
  };

  const closeSettings = ({ restoreFocus = true } = {}) => {
    const { dialog } = getElements();
    if (!dialog.open) {
      return;
    }

    dialog.close();
    document.body.classList.remove('cookie-consent-settings-open');

    if (restoreFocus) {
      const trigger = state.lastSettingsTrigger;
      window.requestAnimationFrame(() => {
        if (trigger?.isConnected && trigger.getClientRects().length) {
          trigger.focus({ preventScroll: true });
          return;
        }

        Array.from(document.querySelectorAll('[data-cookie-settings-open]'))
          .find((button) => button.getClientRects().length)
          ?.focus({ preventScroll: true });
      });
    }
  };

  function openSettings(options = {}) {
    if (!state.initialized) {
      state.pendingSettingsOptions = options;
      return;
    }

    const {
      dialog,
      dialogStatus,
      externalToggle
    } = getElements();
    const trigger = options.trigger || document.activeElement;

    state.lastSettingsTrigger = trigger instanceof HTMLElement ? trigger : null;
    externalToggle.checked = hasConsent('externalServices');
    setStatus(dialogStatus);

    if (!dialog.open) {
      dialog.showModal();
      document.body.classList.add('cookie-consent-settings-open');
    }

    window.requestAnimationFrame(() => {
      if (
        options.focusCategory === 'externalServices'
        && !externalToggle.disabled
      ) {
        externalToggle.focus({ preventScroll: true });
      } else if (options.focusCategory === 'externalServices') {
        state.root.querySelector('[data-cookie-external-category]')?.focus({
          preventScroll: true
        });
      } else {
        dialog.querySelector('[data-cookie-settings-close]')?.focus({
          preventScroll: true
        });
      }
    });
  }

  const saveLocalNecessaryOnly = (
    metadata,
    idempotencyKey,
    currentReceipt = null,
    operationId = ''
  ) => {
    const shouldWithdraw = isPositiveCookieReceipt(currentReceipt);
    const consent = createStoredConsent({
      externalServices: false,
      evidenceStatus: 'pending',
      metadata,
      pendingEvidence: {
        action: shouldWithdraw ? 'withdraw' : REQUIRED_COOKIE_DECISION,
        idempotencyKey,
        evidenceId: shouldWithdraw ? currentReceipt.evidenceId : null,
        operationId: operationId || null
      }
    });
    writeStoredConsent(consent);
    applyConsent(consent);
    return consent;
  };

  async function saveCookieChoice(externalServices, options = {}) {
    if (!state.initialized) {
      throw createUserError(
        'Настройки cookie ещё загружаются. Повторите действие.',
        'CONSENT_NOT_READY'
      );
    }

    const { bannerStatus, dialogStatus } = getElements();
    const statusElement = options.source === 'dialog' ? dialogStatus : bannerStatus;
    let idempotencyKey = '';
    let operationId = '';

    setCookieBusy(true);
    setStatus(statusElement, 'Сохраняем ваш выбор…');

    try {
      if (externalServices && !state.config.externalServiceEvidenceSupported) {
        throw createUserError(
          'Интерактивную карту пока нельзя включить: Evidence ещё не поддерживает категорию «Внешние сервисы».',
          'COOKIE_CATEGORY_CONTRACT_BLOCKED'
        );
      }

      const metadata = await getCookieMetadata(true);
      const currentReceipt = findCurrentReceipt(COOKIE_KIND);
      operationId = createCookieOperationId(
        externalServices,
        metadata,
        currentReceipt
      );
      idempotencyKey = getPendingIdempotencyKey(
        operationId,
        externalServices ? 'cookie-grant' : 'cookie-reject'
      );
      const receipt = await recordCookieEvidence(
        externalServices,
        metadata,
        idempotencyKey
      );
      clearPendingIdempotencyKey(operationId, idempotencyKey);
      const consent = createStoredConsent({
        externalServices,
        evidenceStatus: 'recorded',
        evidenceId: receipt.evidenceId,
        metadata
      });

      writeStoredConsent(consent);
      applyConsent(consent);
      hideBanner();
      closeSettings();
      if (!externalServices) {
        clearPendingIdempotencyPrefix('cookie|grant|');
      }
      return consent;
    } catch (error) {
      if (!externalServices) {
        if (!idempotencyKey) {
          idempotencyKey = createIdempotencyKey('cookie-reject');
        }
        const consent = saveLocalNecessaryOnly(
          state.cookieMetadata,
          idempotencyKey,
          findCurrentReceipt(COOKIE_KIND),
          operationId
        );
        hideBanner();
        closeSettings();
        clearPendingIdempotencyPrefix('cookie|grant|');
        return consent;
      }

      setStatus(statusElement, error.userMessage, 'error');
      throw error;
    } finally {
      setCookieBusy(false);
    }
  }

  const retryPendingRejection = async (storedConsent) => {
    const pending = storedConsent?.pendingEvidence;
    if (
      storedConsent?.externalServices !== false
      || storedConsent?.evidenceStatus !== 'pending'
      || ![REQUIRED_COOKIE_DECISION, 'withdraw'].includes(pending?.action)
      || !pending.idempotencyKey
      || (pending.action === 'withdraw' && !pending.evidenceId)
    ) {
      return;
    }

    try {
      const metadata = await getCookieMetadata();
      await refreshCurrentEvidence();
      const current = findCurrentReceipt(COOKIE_KIND);
      let idempotencyKey = pending.idempotencyKey;
      let operationId = pending.operationId || '';
      let receipt = null;

      if (
        current?.decision === 'withdrawn'
        || (
          current?.decision === REQUIRED_COOKIE_DECISION
          && receiptHasVersion(current, metadata.policy)
          && receiptHasVersion(current, metadata.registry)
        )
      ) {
        receipt = current;
      } else {
        const nextOperationId = createCookieOperationId(false, metadata, current);
        const nextIdempotencyKey = getPendingIdempotencyKey(
          nextOperationId,
          'cookie-reject-retry'
        );
        const latest = readStoredConsent();

        if (latest?.pendingEvidence?.idempotencyKey !== pending.idempotencyKey) {
          return;
        }
        if (operationId && operationId !== nextOperationId) {
          clearPendingIdempotencyKey(operationId, idempotencyKey);
        }

        operationId = nextOperationId;
        idempotencyKey = nextIdempotencyKey;
        latest.pendingEvidence = {
          action: isPositiveCookieReceipt(current)
            ? 'withdraw'
            : REQUIRED_COOKIE_DECISION,
          idempotencyKey,
          evidenceId: isPositiveCookieReceipt(current)
            ? current.evidenceId
            : null,
          operationId
        };
        writeStoredConsent(latest);
        applyConsent(latest);

        receipt = await recordCookieEvidence(false, metadata, idempotencyKey);
      }

      const latest = readStoredConsent();
      if (latest?.pendingEvidence?.idempotencyKey !== idempotencyKey) {
        return;
      }
      if (operationId) {
        clearPendingIdempotencyKey(operationId, idempotencyKey);
      }
      const consent = createStoredConsent({
        externalServices: false,
        evidenceStatus: 'recorded',
        evidenceId: receipt.evidenceId,
        metadata
      });
      writeStoredConsent(consent);
      applyConsent(consent);
      hideBanner();
    } catch (error) {
      // Keep the explicit local rejection and retry on a later visit.
    }
  };

  const ensureEvidenceForMaterial = async ({
    kind,
    decision,
    metadata,
    purposeIds = null
  }) => {
    let current = findCurrentReceipt(kind, purposeIds);
    const operationPrefix = `legal|${kind}|`;
    const alreadyCurrent = (
      current?.decision === decision
      && receiptHasVersion(current, metadata)
    );

    if (alreadyCurrent) {
      clearPendingIdempotencyPrefix(operationPrefix);
      return current;
    }

    const payload = {
      kind,
      context: state.config.context,
      decision,
      locale: state.config.locale,
      purposeIds,
      cookieDecisions: null,
      legalVersions: [{
        materialType: metadata.materialType,
        versionId: metadata.versionId
      }],
      supersedesEvidenceId: current?.evidenceId || null
    };
    const operationId = operationPrefix + [
      decision,
      metadata.versionId,
      (purposeIds || []).join(',') || 'none',
      current?.evidenceId || 'root'
    ].join('|');
    const idempotencyKey = getPendingIdempotencyKey(
      operationId,
      kind === POLICY_KIND ? 'policy-ack' : 'processing-consent'
    );

    try {
      current = await captureWithConflictRecovery(
        payload,
        idempotencyKey,
        (receipt) => (
          receipt.decision === decision
          && receiptHasVersion(receipt, metadata)
        )
      );
    } catch (error) {
      throw createUserError(
        'Не удалось зафиксировать обязательное подтверждение. Заявка не отправлена.',
        'LEGAL_EVIDENCE_CAPTURE_FAILED',
        error
      );
    }
    clearPendingIdempotencyKey(operationId, idempotencyKey);
    return current;
  };

  async function ensureLegalEvidence({
    policyAcknowledged,
    processingConsentGranted
  } = {}) {
    if (!state.initialized || !policyAcknowledged || !processingConsentGranted) {
      throw createUserError(
        'Подтвердите ознакомление с политикой и согласие на обработку персональных данных.',
        'LEGAL_CONSENT_REQUIRED'
      );
    }

    let policyMetadata;
    let processingMetadata;

    try {
      [policyMetadata, processingMetadata] = await Promise.all([
        getMetadata('personalDataPolicy', true),
        getMetadata('personalDataConsent', true)
      ]);
    } catch (error) {
      throw createUserError(
        'Не удалось получить актуальные версии документов. Заявка не отправлена.',
        'LEGAL_METADATA_UNAVAILABLE',
        error
      );
    }

    if (readVisitorToken()) {
      try {
        await refreshCurrentEvidence();
      } catch (error) {
        throw createUserError(
          'Не удалось проверить текущие подтверждения. Заявка не отправлена.',
          'LEGAL_PROJECTION_UNAVAILABLE',
          error
        );
      }
    }

    await ensureEvidenceForMaterial({
      kind: POLICY_KIND,
      decision: 'acknowledged',
      metadata: policyMetadata
    });
    const processingReceipt = await ensureEvidenceForMaterial({
      kind: PROCESSING_KIND,
      decision: 'granted',
      metadata: processingMetadata,
      purposeIds: [state.config.personalDataPurposeId]
    });

    window.dispatchEvent(new CustomEvent('kf21:evidence-ready', {
      detail: getLegalConsentState()
    }));

    return {
      policy: findCurrentReceipt(POLICY_KIND),
      processingConsent: processingReceipt
    };
  }

  const openPolicy = (trigger, materialKey, messages) => {
    const viewer = window.KF21PdfViewer;
    const { banner, bannerStatus, dialogStatus } = getElements();
    const isDialogTrigger = Boolean(
      trigger.closest('[data-cookie-settings-dialog]')
    );
    const isExternalTrigger = !state.root.contains(trigger);
    const restoreHiddenBanner = isExternalTrigger && banner.hidden;
    const targetStatus = isDialogTrigger ? dialogStatus : bannerStatus;

    if (!viewer?.openFromRequest) {
      setStatus(
        targetStatus,
        'Просмотрщик документа ещё загружается. Повторите попытку.',
        'error'
      );
      return false;
    }

    setStatus(targetStatus, messages.loading);
    getMetadata(materialKey, true)
      .then((policy) => {
        if (restoreHiddenBanner) {
          hideBanner();
        } else {
          setStatus(targetStatus);
        }
        viewer.openFromRequest(
          policy.contentUrl,
          { cache: 'no-store' },
          {
            documentKind: 'policy',
            allowDownload: false,
            allowOpenSeparate: false,
            fileName: state.config.materials[materialKey].fileName,
            trigger
          }
        );
      })
      .catch(() => {
        const message = messages.error;
        if (isExternalTrigger) {
          showBanner(message, 'error');
          return;
        }
        setStatus(targetStatus, message, 'error');
      });

    if (isExternalTrigger) {
      showBanner(
        messages.loading
      );
    }
    return true;
  };

  const openCookiePolicy = (trigger) => openPolicy(
    trigger,
    'cookiePolicy',
    {
      loading: 'Получаем актуальную версию политики cookie…',
      error: 'Не удалось получить актуальную версию политики cookie. Внешние сервисы остаются выключенными.'
    }
  );

  const openPersonalDataPolicy = (trigger) => openPolicy(
    trigger,
    'personalDataPolicy',
    {
      loading: 'Получаем актуальную версию политики обработки персональных данных…',
      error: 'Не удалось получить актуальную версию политики обработки персональных данных.'
    }
  );

  const handleDialogKeydown = (event) => {
    const { dialog } = getElements();
    if (!dialog.open || event.key !== 'Tab') {
      return;
    }

    const focusable = Array.from(dialog.querySelectorAll(
      'button:not(:disabled), input:not(:disabled), [href], [tabindex]:not([tabindex="-1"])'
    )).filter((element) => element.getClientRects().length);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!first || !last) {
      return;
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const bindInteractions = () => {
    const {
      dialog,
      dialogPanel,
      externalToggle
    } = getElements();

    document.querySelectorAll('[data-cookie-settings-open]').forEach((button) => {
      button.addEventListener('click', () => openSettings({ trigger: button }));
    });
    document.querySelectorAll('[data-cookie-policy-open]').forEach((control) => {
      control.addEventListener('click', (event) => {
        if (openCookiePolicy(control)) {
          event.preventDefault();
        }
      });
    });
    document.querySelectorAll('[data-personal-data-policy-open]').forEach((control) => {
      control.addEventListener('click', (event) => {
        if (openPersonalDataPolicy(control)) {
          event.preventDefault();
        }
      });
    });
    state.root.querySelectorAll('[data-cookie-accept-all], [data-cookie-dialog-accept-all]')
      .forEach((button) => {
        button.addEventListener('click', () => {
          externalToggle.checked = true;
          saveCookieChoice(true, {
            source: button.closest('[data-cookie-settings-dialog]') ? 'dialog' : 'banner'
          }).catch(() => {});
        });
      });
    state.root.querySelectorAll(
      '[data-cookie-necessary-only], [data-cookie-dialog-necessary-only]'
    ).forEach((button) => {
      button.addEventListener('click', () => {
        externalToggle.checked = false;
        saveCookieChoice(false, {
          source: button.closest('[data-cookie-settings-dialog]') ? 'dialog' : 'banner'
        }).catch(() => {});
      });
    });
    state.root.querySelector('[data-cookie-save-settings]')
      ?.addEventListener('click', () => {
        saveCookieChoice(externalToggle.checked, { source: 'dialog' }).catch(() => {});
      });
    state.root.querySelector('[data-cookie-settings-close]')
      ?.addEventListener('click', () => closeSettings());

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeSettings();
    });
    dialog.addEventListener('click', (event) => {
      if (!dialogPanel.contains(event.target)) {
        closeSettings();
      }
    });
    dialog.addEventListener('close', () => {
      document.body.classList.remove('cookie-consent-settings-open');
    });
    dialog.addEventListener('keydown', handleDialogKeydown);
  };

  const parseConfig = (root) => {
    const apiBaseUrl = root.dataset.apiBaseUrl?.trim() || '';

    return {
      schemaVersion: Number(root.dataset.schemaVersion),
      storageKey: root.dataset.storageKey,
      visitorStorageKey: root.dataset.visitorStorageKey,
      visitorExpiresInDays: Number(root.dataset.visitorExpiresInDays),
      idempotencyStorageKey: root.dataset.idempotencyStorageKey,
      apiBaseUrl,
      context: root.dataset.context,
      locale: root.dataset.locale,
      expiresInDays: Number(root.dataset.expiresInDays),
      consentsUrl: root.dataset.consentsUrl,
      currentConsentsUrl: root.dataset.currentConsentsUrl,
      withdrawalsUrl: root.dataset.withdrawalsUrl,
      personalDataPurposeId: root.dataset.personalDataPurposeId,
      externalServiceCategory: root.dataset.externalServiceCategory,
      externalServiceEvidenceSupported: (
        root.dataset.externalServiceEvidenceSupported === 'true'
      ),
      materials: {
        personalDataPolicy: {
          materialType: root.dataset.personalDataPolicyMaterialType,
          metadataUrl: root.dataset.personalDataPolicyMetadataUrl,
          contentUrl: root.dataset.personalDataPolicyContentUrl,
          fileName: root.dataset.personalDataPolicyFileName
        },
        personalDataConsent: {
          materialType: root.dataset.personalDataConsentMaterialType,
          metadataUrl: root.dataset.personalDataConsentMetadataUrl,
          contentUrl: root.dataset.personalDataConsentContentUrl,
          fileName: root.dataset.personalDataConsentFileName
        },
        cookiePolicy: {
          materialType: root.dataset.cookiePolicyMaterialType,
          metadataUrl: root.dataset.cookiePolicyMetadataUrl,
          contentUrl: root.dataset.cookiePolicyContentUrl,
          fileName: root.dataset.cookiePolicyFileName
        },
        cookieRegistry: {
          materialType: root.dataset.cookieRegistryMaterialType,
          metadataUrl: root.dataset.cookieRegistryMetadataUrl
        }
      }
    };
  };

  const initialize = async () => {
    const root = document.querySelector('[data-cookie-consent-root]');
    if (!root) {
      return;
    }

    state.root = root;
    state.config = parseConfig(root);
    bindInteractions();

    const stored = readStoredConsent();
    if (stored?.externalServices === false) {
      state.consent = stored;
    } else {
      state.consent = createStoredConsent({
        externalServices: false,
        evidenceStatus: 'missing',
        metadata: null
      });
    }

    const cookieMetadataResult = await Promise.allSettled([
      getCookieMetadata(),
      refreshCurrentEvidence()
    ]);
    const metadataAvailable = cookieMetadataResult[0].status === 'fulfilled';
    const evidenceAvailable = cookieMetadataResult[1].status === 'fulfilled';

    state.currentEvidenceAvailable = evidenceAvailable;
    state.initialized = true;

    if (stored?.externalServices === true) {
      if (
        metadataAvailable
        && evidenceAvailable
        && canRestorePositiveConsent(stored, state.cookieMetadata)
      ) {
        state.consent = stored;
        hideBanner();
      } else {
        state.consent = createStoredConsent({
          externalServices: false,
          evidenceStatus: 'missing',
          metadata: state.cookieMetadata
        });
        showBanner(
          'Не удалось подтвердить актуальность прежнего разрешения. Внешние сервисы выключены.',
          'error'
        );
      }
    } else if (stored?.externalServices === false) {
      state.consent = stored;
      if (metadataAvailable && !storedVersionsMatch(stored, state.cookieMetadata)) {
        showBanner(
          'Политика использования cookie обновилась. Пожалуйста, подтвердите выбор ещё раз.'
        );
      } else {
        hideBanner();
      }
    } else if (!metadataAvailable) {
      showBanner(
        'Не удалось получить актуальные версии cookie-документов. Внешние сервисы заблокированы.',
        'error'
      );
    } else if (!state.config.externalServiceEvidenceSupported) {
      showBanner(
        'Интерактивная карта временно недоступна: Evidence ещё не поддерживает категорию «Внешние сервисы».',
        'error'
      );
    } else {
      showBanner();
    }

    document.documentElement.dataset.cookieEvidenceStatus = (
      state.consent.evidenceStatus || 'missing'
    );
    dispatchConsentEvent('kf21:consent-ready');
    window.dispatchEvent(new CustomEvent('kf21:evidence-ready', {
      detail: getLegalConsentState()
    }));

    if (stored?.evidenceStatus === 'pending' && stored.externalServices === false) {
      retryPendingRejection(stored);
    }

    if (state.pendingSettingsOptions) {
      const options = state.pendingSettingsOptions;
      state.pendingSettingsOptions = null;
      openSettings(options);
    }
  };

  document.addEventListener('DOMContentLoaded', initialize, { once: true });
})();
